import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Save, X, Edit2, Edit, Trash2, Eye, EyeOff, PlusCircle, MinusCircle, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Printer, UserMinus, UserPlus, Copy, Settings } from 'lucide-react';
import { ModuleInfo } from './ui/module-info';
import { usePersistedState } from '../hooks/usePersistedState';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import api from '../services/api';
import { formatarHoras, formatarHorasSemSinal } from '../utils/formatarHoras';
import { calcularMetricasHorario, ajustarMetricasBackend } from '../utils/calcularHorarios';
import { 
  getDaysInPeriod, 
  isWeekend, 
  isFeriado, 
  calcularSemanas,
  calcularDiasUteis,
  calcularHorasEsperadas,
  arredondar15min,
  getDiaSemana, 
  formatarDataExibicao 
} from '../utils/horarioUtils';
import { SECCOES, TIPOS_DIA, TEMPLATES_TURNOS, CABECALHO_PADRAO, FERIADOS_PT } from '../constants/horarioConstants';
import HorarioQuickMenu from './HorarioQuickMenu';
import { HorarioRelatorioAcessosModal, HorarioHistoricoBancoModal, HorarioCabecalhoModal, HorarioAdicionarTurnoModal, HorarioVisualizacaoModal } from './horario';
import HorarioGestaoEditor from './horario/HorarioGestaoEditor';
import HorarioGestaoLista from './horario/HorarioGestaoLista';
import HorarioVistaAtual from './horario/HorarioVistaAtual';
import { generatePrintHTML, openPrintWindow } from './horario/horarioPrintUtils';

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';

// NOTA: Usar getBackendUrl() directamente nas chamadas API, não guardar em constante

// Constantes para dias da semana
const diasSemana = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
const diasSemanaKey = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];
const coresColaboradores = [
  'bg-blue-100', 'bg-green-100', 'bg-yellow-100', 'bg-purple-100', 
  'bg-pink-100', 'bg-orange-100', 'bg-teal-100', 'bg-indigo-100'
];

const HorarioTab = ({ readOnly = false, onHorarioAtualizado }) => {
  // Estado para alternar entre sistema atual e gestão de horários
  const viewModeHorario = 'atual'; // Simplificado: apenas vista de horarios mensais
  const [gestaoSubTab, setGestaoSubTab] = useState('modelos'); // Apenas 'modelos' agora
  
  // Estados para Gestão de Horários (Modelos)
  const [modelos, setModelos] = useState([]);
  const [loadingModelos, setLoadingModelos] = useState(false);
  const [semanasPublicaveis, setSemanasPublicaveis] = useState([]);
  const [loadingGestao, setLoadingGestao] = useState(false);
  const [colaboradoresAtivos, setColaboradoresAtivos] = useState([]);
  const [anoFiltroGestao, setAnoFiltroGestao] = usePersistedState('horario_anoGestao', new Date().getFullYear());
  const [mesFiltroGestao, setMesFiltroGestao] = usePersistedState('horario_mesGestao', new Date().getMonth() + 1);
  
  // Estados para modo de edição de Gestão (reutiliza o editor completo)
  const [modoEdicaoGestao, setModoEdicaoGestao] = useState(null); // null, 'semana', 'modelo'
  const [semanaGestaoEditando, setSemanaGestaoEditando] = useState(null);
  const [modeloGestaoEditando, setModeloGestaoEditando] = useState(null);
  const [semanaModeloAtual, setSemanaModeloAtual] = useState(0); // Para modelos com múltiplas semanas
  
  // Estados para modais de Gestão (simplificados - só para copiar)
  const [showCopiarModeloDialog, setShowCopiarModeloDialog] = useState(false);
  const [showCopiarModeloHorarioDialog, setShowCopiarModeloHorarioDialog] = useState(false);
  const [modeloParaCopiar, setModeloParaCopiar] = useState(null);
  const [semanaModeloIdx, setSemanaModeloIdx] = useState(0);
  const [semanaDestinoIdx, setSemanaDestinoIdx] = useState(null);
  
  const [horarios, setHorarios] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false); // Estado para controlar salvamento
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editingHorario, setEditingHorario] = useState(null);
  const [viewingHorario, setViewingHorario] = useState(null);
  const [modalTurno, setModalTurno] = useState(null); // {colabId, day, seccao}
  const [isTrocaModal, setIsTrocaModal] = useState(false); // Estado para checkbox de troca

  // Estados para relatório de acessos
  const [showRelatorioAcessos, setShowRelatorioAcessos] = useState(false);
  const [relatorioAcessos, setRelatorioAcessos] = useState(null);
  const [loadingRelatorio, setLoadingRelatorio] = useState(false);

  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFim, setPeriodoFim] = useState('');
  const [colaboradoresHorarios, setColaboradoresHorarios] = useState([]);
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [semanas, setSemanas] = useState(0);
  const [horasEsperadas, setHorasEsperadas] = useState(0);
  
  // Estados para menu rápido
  const [quickMenuPosition, setQuickMenuPosition] = useState(null);
  const [quickMenuData, setQuickMenuData] = useState(null);
  const [isTrocaQuickMenu, setIsTrocaQuickMenu] = useState(false);
  const [showModalCabecalho, setShowModalCabecalho] = useState(false);
  const [cabecalhoEditavel, setCabecalhoEditavel] = useState('');
  
  // Estado para modo de visualização durante edição
  const [modoExcel, setModoExcel] = useState(true); // SEMPRE Excel
  
  // Estado para importação de Excel
  const [importandoExcel, setImportandoExcel] = useState(false);
  const fileInputRef = React.useRef(null);
  
  // Função para abrir menu ao clicar na célula (Modo Excel)
  const handleCellClick = (e, colab, dia) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setQuickMenuPosition({
      top: rect.bottom,
      left: rect.left
    });
    setQuickMenuData({
      colaborador: colab,
      dia: dia,
      colaborador_id: colab.colaborador_id,
      data: dia
    });
  };
  
  // TEMPLATES_TURNOS - agora importado de horarioConstants

  useEffect(() => {
    checkUserRole();
    loadHorarios();
    loadColaboradores();
    carregarCabecalho();
  }, []);
  
  const carregarCabecalho = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${getBackendUrl()}/api/configuracoes/cabecalho-horarios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCabecalhoEditavel(response.data.cabecalho || CABECALHO_PADRAO);
    } catch (error) {
      console.error('Erro ao carregar cabeçalho:', error);
      // Fallback para localStorage se API falhar
      const cabecalhoSalvo = localStorage.getItem('cabecalho_impressao');
      if (cabecalhoSalvo) {
        setCabecalhoEditavel(cabecalhoSalvo);
      } else {
        setCabecalhoEditavel(CABECALHO_PADRAO);
      }
    }
  };

  const checkUserRole = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    // Se readOnly=true, sempre considera como não-admin
    setIsAdmin(!readOnly && userData.role === 'admin');
  };

  const loadHorarios = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await api.get(`${getBackendUrl()}/api/horarios-mensais`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHorarios(response.data);
    } catch (err) {
      console.error('Erro ao carregar horários:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadColaboradores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${getBackendUrl()}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setColaboradores(response.data);
      // Também guardar para gestão de horários
      setColaboradoresAtivos(response.data.filter(c => c.ativo !== false && c.role !== 'admin'));
    } catch (err) {
      console.error('Erro ao carregar colaboradores:', err);
    }
  };

  // ==================== FUNÇÕES DE GESTÃO DE HORÁRIOS ====================
  
  // Carregar modelos
  const carregarModelos = async () => {
    try {
      setLoadingModelos(true);
      const token = localStorage.getItem('token');
      const response = await api.get(`${getBackendUrl()}/api/horarios/modelos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModelos(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
      setModelos([]);
    } finally {
      setLoadingModelos(false);
    }
  };
  
  // Carregar semanas publicáveis
  const carregarSemanasPublicaveis = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${getBackendUrl()}/api/horarios/semanas`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { ano: anoFiltroGestao, mes: mesFiltroGestao }
      });
      setSemanasPublicaveis(response.data);
    } catch (error) {
      console.error('Erro ao carregar semanas:', error);
    }
  };
  
  // Carregar dados de gestão quando mudar para esse modo
  useEffect(() => {
    if (viewModeHorario === 'gestao' && isAdmin) {
      setLoadingGestao(true);
      Promise.all([carregarModelos(), carregarSemanasPublicaveis()])
        .finally(() => setLoadingGestao(false));
    }
  }, [viewModeHorario, isAdmin]);
  
  // Recarregar semanas quando filtros mudarem
  useEffect(() => {
    if (viewModeHorario === 'gestao' && gestaoSubTab === 'semanas') {
      carregarSemanasPublicaveis();
    }
  }, [anoFiltroGestao, mesFiltroGestao, gestaoSubTab]);
  
  // Carregar modelos quando o diálogo é aberto (para garantir que estão disponíveis)
  useEffect(() => {
    if (showCopiarModeloDialog || showCopiarModeloHorarioDialog) {
      if (modelos.length === 0) {
        carregarModelos();
      }
    }
  }, [showCopiarModeloDialog, showCopiarModeloHorarioDialog]);
  
  // ==================== FUNÇÕES DE GESTÃO - USAR EDITOR COMPLETO ====================
  
  // Criar novo modelo - abre o editor completo
  const novoModelo = () => {
    // Calcular uma semana exemplo (próxima segunda a domingo)
    const hoje = new Date();
    const diasAteSegunda = (8 - hoje.getDay()) % 7 || 7;
    const proximaSegunda = new Date(hoje);
    proximaSegunda.setDate(hoje.getDate() + diasAteSegunda);
    const proximoDomingo = new Date(proximaSegunda);
    proximoDomingo.setDate(proximaSegunda.getDate() + 6);
    
    // Criar estrutura de colaboradores igual ao horário mensal
    const colaboradoresVisiveis = colaboradores.filter(c => c.ativo !== false && c.mostrar_em_horario !== false && c.role !== 'admin');
    const colabsEstrutura = colaboradoresVisiveis.map(colab => ({
      colaborador_id: colab.id,
      colaborador_nome: colab.nome || colab.username,
      seccao: colab.loja || 'Café',
      funcao: colab.funcao || 'Empregado',
      mostrar_em_horario: true,
      registos_por_dia: {},
      total_horas: 0,
      horas_feriado: 0,
      dias_ferias: 0,
      dias_falta: 0,
      dias_folga: 0,
      banco_horas_anterior: 0,
      banco_horas: 0
    }));
    
    setModeloGestaoEditando({
      nome: 'Novo Modelo',
      descricao: '',
      num_semanas: 1,
      semanas: [{
        data_inicio: proximaSegunda.toISOString().split('T')[0],
        data_fim: proximoDomingo.toISOString().split('T')[0],
        colaboradores_horarios: colabsEstrutura
      }]
    });
    setSemanaModeloAtual(0);
    setPeriodoInicio(proximaSegunda.toISOString().split('T')[0]);
    setPeriodoFim(proximoDomingo.toISOString().split('T')[0]);
    setColaboradoresHorarios(colabsEstrutura);
    setModoEdicaoGestao('modelo');
  };
  
  // Editar modelo existente - abre o editor completo
  const editarModelo = async (modelo) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${getBackendUrl()}/api/horarios/modelos/${modelo.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const modeloCompleto = response.data;
      
      // Converter estrutura do modelo para estrutura do editor
      const semanaAtual = modeloCompleto.semanas?.[0] || {};
      
      // Criar estrutura de colaboradores
      const colaboradoresVisiveis = colaboradores.filter(c => c.ativo !== false && c.mostrar_em_horario !== false && c.role !== 'admin');
      const colabsEstrutura = colaboradoresVisiveis.map(colab => {
        // Procurar dados existentes no modelo
        const turnoExistente = semanaAtual.colaboradores_horarios?.find(
          t => t.colaborador_id === colab.id
        );
        
        return {
          colaborador_id: colab.id,
          colaborador_nome: colab.nome || colab.username,
          seccao: turnoExistente?.seccao || colab.loja || 'Café',
          funcao: colab.funcao || 'Empregado',
          mostrar_em_horario: true,
          registos_por_dia: turnoExistente?.registos_por_dia || {},
          total_horas: turnoExistente?.total_horas || 0,
          horas_feriado: 0,
          dias_ferias: 0,
          dias_falta: 0,
          dias_folga: 0,
          banco_horas_anterior: 0,
          banco_horas: 0
        };
      });
      
      // Usar datas da semana guardada ou calcular novas
      const dataInicio = semanaAtual.data_inicio || new Date().toISOString().split('T')[0];
      const dataFim = semanaAtual.data_fim || new Date().toISOString().split('T')[0];
      
      setModeloGestaoEditando({
        ...modeloCompleto,
        semanas: modeloCompleto.semanas?.map((s, idx) => ({
          ...s,
          colaboradores_horarios: idx === 0 ? colabsEstrutura : (s.colaboradores_horarios || [])
        })) || [{ colaboradores_horarios: colabsEstrutura }]
      });
      setSemanaModeloAtual(0);
      setPeriodoInicio(dataInicio);
      setPeriodoFim(dataFim);
      setColaboradoresHorarios(colabsEstrutura);
      setModoEdicaoGestao('modelo');
    } catch (error) {
      console.error('Erro ao carregar modelo:', error);
      toast.error('Erro ao carregar modelo');
    }
  };
  
  // Guardar modelo a partir do editor
  const guardarModeloEditor = async () => {
    if (!modeloGestaoEditando) return;
    
    try {
      setIsSaving(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Atualizar a semana atual com os dados do editor
      const modeloAtualizado = {
        ...modeloGestaoEditando,
        semanas: modeloGestaoEditando.semanas.map((s, idx) => 
          idx === semanaModeloAtual 
            ? { ...s, data_inicio: periodoInicio, data_fim: periodoFim, colaboradores_horarios: colaboradoresHorarios }
            : s
        )
      };
      
      if (modeloGestaoEditando.id) {
        await api.put(`${getBackendUrl()}/api/horarios/modelos/${modeloGestaoEditando.id}`, modeloAtualizado, { headers });
        toast.success('Modelo atualizado!');
      } else {
        await api.post(`${getBackendUrl()}/api/horarios/modelos`, modeloAtualizado, { headers });
        toast.success('Modelo criado!');
      }
      
      setModoEdicaoGestao(null);
      setModeloGestaoEditando(null);
      carregarModelos();
    } catch (error) {
      console.error('Erro ao guardar modelo:', error);
      toast.error('Erro ao guardar modelo');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Eliminar modelo
  const eliminarModelo = async (modeloId) => {
    if (!confirm('Tem a certeza que deseja eliminar este modelo?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.delete(`${getBackendUrl()}/api/horarios/modelos/${modeloId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Modelo eliminado!');
      carregarModelos();
    } catch (error) {
      console.error('Erro ao eliminar modelo:', error);
      toast.error('Erro ao eliminar modelo');
    }
  };
  
  // Criar nova semana publicável - abre o editor completo
  const novaSemanaPublicavel = () => {
    // Calcular próxima segunda-feira
    const hoje = new Date();
    const diasAteSegunda = (8 - hoje.getDay()) % 7 || 7;
    const proximaSegunda = new Date(hoje);
    proximaSegunda.setDate(hoje.getDate() + diasAteSegunda);
    const proximoDomingo = new Date(proximaSegunda);
    proximoDomingo.setDate(proximaSegunda.getDate() + 6);
    
    // Criar estrutura de colaboradores igual ao horário mensal
    const colaboradoresVisiveis = colaboradores.filter(c => c.ativo !== false && c.mostrar_em_horario !== false && c.role !== 'admin');
    const colabsEstrutura = colaboradoresVisiveis.map(colab => ({
      colaborador_id: colab.id,
      colaborador_nome: colab.nome || colab.username,
      seccao: colab.loja || 'Café',
      funcao: colab.funcao || 'Empregado',
      mostrar_em_horario: true,
      registos_por_dia: {},
      total_horas: 0,
      horas_feriado: 0,
      dias_ferias: 0,
      dias_falta: 0,
      dias_folga: 0,
      banco_horas_anterior: 0,
      banco_horas: 0
    }));
    
    setSemanaGestaoEditando({
      data_inicio: proximaSegunda.toISOString().split('T')[0],
      data_fim: proximoDomingo.toISOString().split('T')[0],
      status: 'rascunho',
      colaboradores_horarios: colabsEstrutura
    });
    setPeriodoInicio(proximaSegunda.toISOString().split('T')[0]);
    setPeriodoFim(proximoDomingo.toISOString().split('T')[0]);
    setColaboradoresHorarios(colabsEstrutura);
    setModoEdicaoGestao('semana');
  };
  
  // Editar semana existente - abre o editor completo
  const editarSemanaPublicavel = async (semana) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${getBackendUrl()}/api/horarios/semanas/${semana.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const semanaCompleta = response.data;
      
      // Criar estrutura de colaboradores
      const colaboradoresVisiveis = colaboradores.filter(c => c.ativo !== false && c.mostrar_em_horario !== false && c.role !== 'admin');
      const colabsEstrutura = colaboradoresVisiveis.map(colab => {
        // Procurar dados existentes na semana
        const colabExistente = semanaCompleta.colaboradores_horarios?.find(
          t => t.colaborador_id === colab.id
        );
        
        return {
          colaborador_id: colab.id,
          colaborador_nome: colab.nome || colab.username,
          seccao: colabExistente?.seccao || colab.loja || 'Café',
          funcao: colab.funcao || 'Empregado',
          mostrar_em_horario: true,
          registos_por_dia: colabExistente?.registos_por_dia || {},
          total_horas: colabExistente?.total_horas || 0,
          horas_feriado: 0,
          dias_ferias: 0,
          dias_falta: 0,
          dias_folga: 0,
          banco_horas_anterior: 0,
          banco_horas: 0
        };
      });
      
      setSemanaGestaoEditando({
        ...semanaCompleta,
        colaboradores_horarios: colabsEstrutura
      });
      setPeriodoInicio(semanaCompleta.data_inicio);
      setPeriodoFim(semanaCompleta.data_fim);
      setColaboradoresHorarios(colabsEstrutura);
      setModoEdicaoGestao('semana');
    } catch (error) {
      console.error('Erro ao carregar semana:', error);
      toast.error('Erro ao carregar semana');
    }
  };
  
  // Guardar semana a partir do editor
  const guardarSemanaEditor = async () => {
    if (!semanaGestaoEditando) return;
    
    try {
      setIsSaving(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const semanaAtualizada = {
        ...semanaGestaoEditando,
        data_inicio: periodoInicio,
        data_fim: periodoFim,
        colaboradores_horarios: colaboradoresHorarios
      };
      
      if (semanaGestaoEditando.id) {
        await api.put(`${getBackendUrl()}/api/horarios/semanas/${semanaGestaoEditando.id}`, semanaAtualizada, { headers });
        toast.success('Horário semanal atualizado!');
      } else {
        await api.post(`${getBackendUrl()}/api/horarios/semanas`, semanaAtualizada, { headers });
        toast.success('Horário semanal criado!');
      }
      
      setModoEdicaoGestao(null);
      setSemanaGestaoEditando(null);
      carregarSemanasPublicaveis();
    } catch (error) {
      console.error('Erro ao guardar semana:', error);
      toast.error(error.response?.data?.detail || 'Erro ao guardar horário');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Cancelar edição
  const cancelarEdicaoGestao = () => {
    setModoEdicaoGestao(null);
    setSemanaGestaoEditando(null);
    setModeloGestaoEditando(null);
    setColaboradoresHorarios([]);
  };
  
  // Eliminar semana
  const eliminarSemanaPublicavel = async (semanaId) => {
    if (!confirm('Tem a certeza que deseja eliminar este horário?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.delete(`${getBackendUrl()}/api/horarios/semanas/${semanaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Horário eliminado!');
      carregarSemanasPublicaveis();
    } catch (error) {
      console.error('Erro ao eliminar semana:', error);
      toast.error('Erro ao eliminar horário');
    }
  };
  
  // Publicar semana
  const publicarSemana = async (semanaId) => {
    try {
      const token = localStorage.getItem('token');
      await api.post(`${getBackendUrl()}/api/horarios/semanas/${semanaId}/publicar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Horário PUBLICADO! Colaboradores já podem ver.');
      carregarSemanasPublicaveis();
    } catch (error) {
      console.error('Erro ao publicar:', error);
      toast.error('Erro ao publicar horário');
    }
  };
  
  // Despublicar semana
  const despublicarSemana = async (semanaId) => {
    try {
      const token = localStorage.getItem('token');
      await api.post(`${getBackendUrl()}/api/horarios/semanas/${semanaId}/despublicar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Horário despublicado. Colaboradores já não veem.');
      carregarSemanasPublicaveis();
    } catch (error) {
      console.error('Erro ao despublicar:', error);
      toast.error('Erro ao despublicar horário');
    }
  };
  
  // Iniciar cópia de modelo
  const iniciarCopiarDeModelo = async (semana = null) => {
    setSemanaGestaoEditando(semana);
    setModeloParaCopiar(null);
    setSemanaModeloIdx(0);
    
    // Garantir que os modelos estão carregados
    if (modelos.length === 0) {
      await carregarModelos();
    }
    
    setShowCopiarModeloDialog(true);
  };
  
  // Executar cópia de modelo
  const executarCopiaDeModelo = async () => {
    if (!modeloParaCopiar) {
      toast.error('Selecione um modelo');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      const dados = {
        modelo_id: modeloParaCopiar.id,
        semana_modelo_idx: semanaModeloIdx
      };
      
      if (semanaGestaoEditando?.id) {
        dados.semana_destino_id = semanaGestaoEditando.id;
      } else {
        // Criar nova semana
        const hoje = new Date();
        const diasAteSegunda = (8 - hoje.getDay()) % 7 || 7;
        const proximaSegunda = new Date(hoje);
        proximaSegunda.setDate(hoje.getDate() + diasAteSegunda);
        const proximoDomingo = new Date(proximaSegunda);
        proximoDomingo.setDate(proximaSegunda.getDate() + 6);
        
        dados.data_inicio = proximaSegunda.toISOString().split('T')[0];
        dados.data_fim = proximoDomingo.toISOString().split('T')[0];
      }
      
      await api.post(`${getBackendUrl()}/api/horarios/semanas/copiar-de-modelo`, dados, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Turnos copiados do modelo "${modeloParaCopiar.nome}"!`);
      setShowCopiarModeloDialog(false);
      carregarSemanasPublicaveis();
    } catch (error) {
      console.error('Erro ao copiar de modelo:', error);
      toast.error(error.response?.data?.detail || 'Erro ao copiar de modelo');
    }
  };
  
  // Abrir diálogo de copiar modelo para horário mensal (garantindo modelos carregados)
  const abrirCopiarModeloHorarioMensal = async () => {
    // Garantir que os modelos estão carregados
    if (modelos.length === 0) {
      await carregarModelos();
    }
    setModeloParaCopiar(null);
    setSemanaDestinoIdx(null);
    setShowCopiarModeloHorarioDialog(true);
  };
  
  // Aplicar modelo ao horário mensal atual
  const aplicarModeloAoHorarioMensal = () => {
    if (!modeloParaCopiar) {
      toast.error('Selecione um modelo');
      return;
    }
    
    if (semanaDestinoIdx === null || semanaDestinoIdx === undefined) {
      toast.error('Selecione a semana de destino');
      return;
    }
    
    const semanaModelo = modeloParaCopiar.semanas?.[semanaModeloIdx];
    if (!semanaModelo) {
      toast.error('Semana do modelo não encontrada');
      return;
    }
    
    // Calcular as semanas do período
    const semanasDoMes = [];
    if (periodoInicio && periodoFim) {
      const inicio = new Date(periodoInicio + 'T00:00:00');
      const fim = new Date(periodoFim + 'T00:00:00');
      
      // Encontrar a primeira segunda-feira
      let d = new Date(inicio);
      while (d.getDay() !== 1 && d <= fim) {
        d.setDate(d.getDate() + 1);
      }
      
      // Agrupar por semanas (Segunda a Domingo)
      while (d <= fim) {
        const inicioSemana = new Date(d);
        const fimSemana = new Date(d);
        fimSemana.setDate(fimSemana.getDate() + 6);
        if (fimSemana > fim) fimSemana.setTime(fim.getTime());
        
        semanasDoMes.push({
          inicio: inicioSemana.toISOString().split('T')[0],
          fim: fimSemana.toISOString().split('T')[0]
        });
        
        d.setDate(d.getDate() + 7);
      }
    }
    
    const semanaDestino = semanasDoMes[semanaDestinoIdx];
    if (!semanaDestino) {
      toast.error('Semana de destino inválida');
      return;
    }
    
    // Mapear dias da semana
    const diasSemana = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
    
    // Aplicar os turnos do modelo APENAS à semana selecionada
    setColaboradoresHorarios(prev => {
      return prev.map(colab => {
        // Encontrar este colaborador no modelo
        const colabModelo = semanaModelo.colaboradores_horarios?.find(
          cm => cm.colaborador_id === colab.colaborador_id
        );
        
        if (!colabModelo || !colabModelo.registos_por_dia) {
          return colab;
        }
        
        const registosAtualizados = { ...colab.registos_por_dia };
        
        // Aplicar apenas aos dias da semana selecionada
        const inicioSemana = new Date(semanaDestino.inicio + 'T00:00:00');
        const fimSemana = new Date(semanaDestino.fim + 'T00:00:00');
        
        for (let d = new Date(inicioSemana); d <= fimSemana; d.setDate(d.getDate() + 1)) {
          const diaSemanaIdx = d.getDay();
          const diaKey = diasSemana[diaSemanaIdx];
          const diaStr = d.toISOString().split('T')[0];
          
          // Se o modelo tem turnos para este dia da semana
          const turnosModelo = colabModelo.registos_por_dia[diaKey];
          if (turnosModelo && turnosModelo.length > 0) {
            registosAtualizados[diaStr] = turnosModelo.map(t => ({ ...t }));
          }
        }
        
        return {
          ...colab,
          registos_por_dia: registosAtualizados
        };
      });
    });
    
    const numSemana = semanaDestinoIdx + 1;
    toast.success(`Modelo "${modeloParaCopiar.nome}" aplicado à Semana ${numSemana}!`);
    setShowCopiarModeloHorarioDialog(false);
    setModeloParaCopiar(null);
    setSemanaDestinoIdx(null);
  };
  
  // Calcular semanas do período para o dialog
  const getSemanasDoMes = () => {
    const semanas = [];
    if (!periodoInicio || !periodoFim) return semanas;
    
    const inicio = new Date(periodoInicio + 'T00:00:00');
    const fim = new Date(periodoFim + 'T00:00:00');
    
    // Encontrar a primeira segunda-feira
    let d = new Date(inicio);
    while (d.getDay() !== 1 && d <= fim) {
      d.setDate(d.getDate() + 1);
    }
    
    let semanaNum = 1;
    while (d <= fim) {
      const inicioSemana = new Date(d);
      const fimSemana = new Date(d);
      fimSemana.setDate(fimSemana.getDate() + 6);
      if (fimSemana > fim) fimSemana.setTime(fim.getTime());
      
      semanas.push({
        idx: semanaNum - 1,
        label: `Semana ${semanaNum} (${inicioSemana.getDate()}/${inicioSemana.getMonth()+1} - ${fimSemana.getDate()}/${fimSemana.getMonth()+1})`,
        inicio: inicioSemana.toISOString().split('T')[0],
        fim: fimSemana.toISOString().split('T')[0]
      });
      
      semanaNum++;
      d.setDate(d.getDate() + 7);
    }
    
    return semanas;
  };
  
  // Formatar data para exibição
  const formatarDataCurta = (dataStr) => {
    if (!dataStr) return '';
    const data = new Date(dataStr);
    return data.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' });
  };

  // getDaysInPeriod, isWeekend, isFeriado, calcularSemanas - agora importados de horarioUtils

  // Effect para calcular semanas e horas esperadas quando período muda
  useEffect(() => {
    if (periodoInicio && periodoFim) {
      const semanasCalc = calcularSemanas(periodoInicio, periodoFim);
      setSemanas(semanasCalc);
      
      // REGRA: HORAS ESPERADAS = Semanas × 40h
      const horasEsperadasCalc = calcularHorasEsperadas(periodoInicio, periodoFim);
      setHorasEsperadas(horasEsperadasCalc);
      
    }
  }, [periodoInicio, periodoFim]);

  // Effect para carregar banco anterior AUTOMATICAMENTE quando período muda
  // REMOVIDO: Busca automática do banco anterior
  // Agora o banco anterior só é carregado quando o utilizador clica no botão "Buscar Banco H Anterior"

  // Buscar banco de horas do mês anterior
  const buscarBancoMesAnterior = async (dataInicio) => {
    try {
      if (!dataInicio) return;
      
      const token = localStorage.getItem('token');
      
      const dataInicioObj = new Date(dataInicio + 'T00:00:00');
      
      // Buscar todos os horários
      const response = await api.get(`${getBackendUrl()}/api/horarios-mensais`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Encontrar o horário que TERMINA IMEDIATAMENTE ANTES do período atual
      // Ordenar por data de fim (mais recente primeiro)
      const horariosOrdenados = response.data
        .filter(h => new Date(h.periodo_fim + 'T00:00:00') < dataInicioObj)
        .sort((a, b) => new Date(b.periodo_fim) - new Date(a.periodo_fim));
      
      horariosOrdenados.forEach(h => {
      });
      
      const horarioAnterior = horariosOrdenados[0]; // Mais recente que termina antes
      
      if (horarioAnterior && horarioAnterior.colaboradores_horarios) {
        
        // Atualizar banco_horas_anterior de cada colaborador
        setColaboradoresHorarios(prev => prev.map(colab => {
          const colabAnterior = horarioAnterior.colaboradores_horarios.find(
            c => c.colaborador_id === colab.colaborador_id
          );
          
          if (colabAnterior) {
            return {
              ...colab,
              banco_horas_anterior: colabAnterior.banco_horas || 0,
              banco_horas_anterior_manual: false
            };
          }
          
          return colab;
        }));
        
        alert(`✅ Banco de horas carregado!\nPeríodo anterior: ${horarioAnterior.periodo_inicio} - ${horarioAnterior.periodo_fim}`);
      } else {
        alert(`ℹ️ Não foi encontrado horário anterior a ${dataInicio}`);
      }
    } catch (err) {
      console.error('❌ Erro ao buscar banco anterior:', err);
      alert('❌ Erro ao buscar banco do horário anterior. Verifique o console.');
    }
  };

  // Mover colaborador para cima
  const moverColaboradorCima = (colabId, seccao) => {
    setColaboradoresHorarios(prev => {
      const colabsSeccao = prev.filter(c => c.seccao === seccao);
      const outrasSecoes = prev.filter(c => c.seccao !== seccao);
      
      const index = colabsSeccao.findIndex(c => c.colaborador_id === colabId);
      if (index > 0) {
        [colabsSeccao[index], colabsSeccao[index - 1]] = [colabsSeccao[index - 1], colabsSeccao[index]];
      }
      
      return [...outrasSecoes, ...colabsSeccao];
    });
  };

  // Mover colaborador para baixo
  const moverColaboradorBaixo = (colabId, seccao) => {
    setColaboradoresHorarios(prev => {
      const colabsSeccao = prev.filter(c => c.seccao === seccao);
      const outrasSecoes = prev.filter(c => c.seccao !== seccao);
      
      const index = colabsSeccao.findIndex(c => c.colaborador_id === colabId);
      if (index < colabsSeccao.length - 1) {
        [colabsSeccao[index], colabsSeccao[index + 1]] = [colabsSeccao[index + 1], colabsSeccao[index]];
      }
      
      return [...outrasSecoes, ...colabsSeccao];
    });
  };

  // Remover colaborador do horário atual
  const removerColaboradorDoHorario = (colabId) => {
    const colab = colaboradoresHorarios.find(c => c.colaborador_id === colabId);
    if (!colab) return;
    
    if (!window.confirm(`⚠️ Remover "${colab.colaborador_nome}" deste horário?\n\nO colaborador deixará de aparecer neste horário mas continuará disponível em outros horários.`)) {
      return;
    }
    
    setColaboradoresHorarios(prev => prev.filter(c => c.colaborador_id !== colabId));
  };

  // Obter colaboradores que não estão no horário atual (para poder adicionar)
  const getColaboradoresDisponiveis = () => {
    const idsNoHorario = colaboradoresHorarios.map(c => c.colaborador_id);
    return colaboradores.filter(c => !idsNoHorario.includes(c.id));
  };

  // Adicionar colaborador ao horário atual
  const adicionarColaboradorAoHorario = (colabId) => {
    const colab = colaboradores.find(c => c.id === colabId);
    if (!colab) return;
    
    const novoColab = {
      colaborador_id: colab.id,
      colaborador_nome: colab.nome,
      seccao: colab.loja || 'Café',
      funcao: colab.funcao || 'Empregado',
      mostrar_em_horario: colab.mostrar_em_horario,
      registos_por_dia: {},
      total_horas: 0,
      horas_feriado: 0,
      dias_ferias: 0,
      dias_falta: 0,
      dias_folga: 0,
      banco_horas_anterior: 0,
      banco_horas: 0
    };
    
    setColaboradoresHorarios(prev => [...prev, novoColab]);
  };

  const calcularHoras = (entrada, saida) => {
    if (!entrada || !saida) return null;
    const [hE, mE] = entrada.split(':').map(Number);
    const [hS, mS] = saida.split(':').map(Number);
    const minutosEntrada = hE * 60 + mE;
    const minutosSaida = hS * 60 + mS;
    const totalMinutos = minutosSaida - minutosEntrada;
    const horas = Math.floor(totalMinutos / 60);
    const minutos = totalMinutos % 60;
    // 🔧 CORREÇÃO: Retornar valor decimal PRECISO (não arredondado)
    // Mantém precisão total dos minutos (ex: 8h30m = 8.5, não 8.50)
    return horas + minutos / 60;
  };

  const addTurno = (colabId, day, seccao, tipo) => {
    // Verificar se é troca
    const eTrocaCheckbox = document.getElementById('e-troca-checkbox');
    const eTroca = eTrocaCheckbox ? eTrocaCheckbox.checked : false;
    
    setColaboradoresHorarios(prev => prev.map(c => {
      if (String(c.colaborador_id) !== String(colabId)) return c;
      
      const registosAtualizados = { ...c.registos_por_dia };
      if (!registosAtualizados[day]) registosAtualizados[day] = [];
      
      // Verificar duplicação
      const jaExiste = registosAtualizados[day].some(t => 
        t.local === seccao && t.tipo === tipo
      );
      
      if (jaExiste) {
        alert('Turno já existe nesta célula!');
        return c;
      }
      
      const novoTurno = {
        local: seccao,
        tipo: tipo,
        observacao: null,
        e_troca: eTroca
      };
      
      if (tipo === 'gestao') {
        novoTurno.horas = 8.0;
        novoTurno.entrada = null;
        novoTurno.saida = null;
      } else if (tipo === 'baixa' || tipo === 'ferias' || tipo === 'folga_compensacao') {
        novoTurno.horas = 8;  // 8h automáticas (folga_compensacao conta 8h para o banco)
        novoTurno.entrada = null;
        novoTurno.saida = null;
      } else if (tipo === 'reuniao') {
        novoTurno.horas = 1.0;
        novoTurno.entrada = null;
        novoTurno.saida = null;
      } else if (tipo === 'folga' || tipo === 'falta') {
        novoTurno.horas = null;
        novoTurno.entrada = null;
        novoTurno.saida = null;
      } else if (tipo === 'normal') {
        novoTurno.entrada = '08:00';
        novoTurno.saida = '17:00';
        novoTurno.horas = parseFloat(calcularHoras('08:00', '17:00'));
      } else {
        novoTurno.entrada = null;
        novoTurno.saida = null;
        novoTurno.horas = null;
      }
      
      registosAtualizados[day].push(novoTurno);
      return { ...c, registos_por_dia: registosAtualizados };
    }));
  };

  // formatarHoras function now imported from utils

  const handleNew = async () => {
    try {
      // Buscar último horário salvo para definir período seguinte
      const token = localStorage.getItem('token');
      const response = await api.get(`${getBackendUrl()}/api/horarios-mensais`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let firstDay, lastDay;
      let ultimoHorario = null; // Declarar fora do bloco if
      
      if (response.data && response.data.length > 0) {
        // Ordenar por data de fim (mais recente primeiro)
        const horariosSorted = response.data.sort((a, b) => 
          new Date(b.periodo_fim) - new Date(a.periodo_fim)
        );
        
        ultimoHorario = horariosSorted[0];
        const ultimaDataFim = new Date(ultimoHorario.periodo_fim + 'T00:00:00');
        
        // Próximo dia após o último horário
        firstDay = new Date(ultimaDataFim);
        firstDay.setDate(firstDay.getDate() + 1);
        
        // Período de 1 mês a partir do dia seguinte
        lastDay = new Date(firstDay);
        lastDay.setMonth(lastDay.getMonth() + 1);
        lastDay.setDate(lastDay.getDate() - 1);
        
      } else {
        // Se não há horários, usar mês atual
        const today = new Date();
        firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      }
      
      const periodoInicioStr = firstDay.toISOString().split('T')[0];
      const periodoFimStr = lastDay.toISOString().split('T')[0];
      
      setPeriodoInicio(periodoInicioStr);
      setPeriodoFim(periodoFimStr);
      
      // Filtrar apenas colaboradores que devem aparecer no horário
      const colaboradoresVisiveis = colaboradores.filter(c => c.ativo !== false && c.mostrar_em_horario !== false);
      
      // Criar estrutura inicial dos colaboradores
      let initialColabs = colaboradoresVisiveis.map(colab => {
        // Se existe horário anterior, tentar usar o colaborador_id de lá (caso tenha mudado)
        let colabId = colab.id;
        if (ultimoHorario && ultimoHorario.colaboradores_horarios) {
          const colabAnterior = ultimoHorario.colaboradores_horarios.find(
            c => c.colaborador_nome === colab.nome
          );
          if (colabAnterior) {
            colabId = colabAnterior.colaborador_id;
          }
        }
        
        return {
          colaborador_id: colabId,
          colaborador_nome: colab.nome,
          seccao: colab.loja || 'Café',
          funcao: colab.funcao || 'Empregado',
          mostrar_em_horario: colab.mostrar_em_horario,
          registos_por_dia: {},
          total_horas: 0,
          horas_feriado: 0,
          dias_ferias: 0,
          dias_falta: 0,
          dias_folga: 0,
          banco_horas_anterior: 0,
          banco_horas: 0
        };
      });
      
      // MANTER ORDEM DO HORÁRIO ANTERIOR
      if (ultimoHorario && ultimoHorario.colaboradores_horarios) {
        const ordemAnterior = ultimoHorario.colaboradores_horarios.map(c => String(c.colaborador_id));
        
        initialColabs.sort((a, b) => {
          const indexA = ordemAnterior.indexOf(String(a.colaborador_id));
          const indexB = ordemAnterior.indexOf(String(b.colaborador_id));
          
          // Se ambos estão na ordem anterior, usar essa ordem
          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          // Se só A está, A vem primeiro
          if (indexA !== -1) return -1;
          // Se só B está, B vem primeiro
          if (indexB !== -1) return 1;
          // Se nenhum está (colaboradores novos), manter ordem alfabética
          return a.colaborador_nome.localeCompare(b.colaborador_nome);
        });
        
      }
      
      setColaboradoresHorarios(initialColabs);
      setEditingHorario(null);
      setShowModal(true);
      
      // REMOVIDO: Busca automática - agora só carrega quando clicar no botão
    } catch (err) {
      console.error('Erro ao preparar novo horário:', err);
      // Fallback para mês atual se der erro
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      
      setPeriodoInicio(firstDay.toISOString().split('T')[0]);
      setPeriodoFim(lastDay.toISOString().split('T')[0]);
      
      // Filtrar apenas colaboradores que devem aparecer no horário
      const colaboradoresVisiveis = colaboradores.filter(c => c.ativo !== false && c.mostrar_em_horario !== false);
      
      const initialColabs = colaboradoresVisiveis.map(colab => ({
        colaborador_id: colab.id,
        colaborador_nome: colab.nome,
        seccao: colab.loja || 'Café',
        funcao: colab.funcao || 'Empregado',
        mostrar_em_horario: colab.mostrar_em_horario,
        registos_por_dia: {},
        total_horas: 0,
        horas_feriado: 0,
        dias_ferias: 0,
        dias_falta: 0,
        dias_folga: 0,
        banco_horas_anterior: 0,
        banco_horas: 0
      }));
      
      setColaboradoresHorarios(initialColabs);
      // Removido setRenderKey - não é necessário, o React já vai re-renderizar
      setEditingHorario(null);
      setShowModal(true);
    }
  };

  const handleEdit = (horario) => {
    setPeriodoInicio(horario.periodo_inicio);
    setPeriodoFim(horario.periodo_fim);
    
    // IMPORTANTE: Fazer deep clone dos dados para evitar referências compartilhadas
    const colaboradoresClonados = (horario.colaboradores_horarios || []).map(colab => ({
      ...colab,
      registos_por_dia: JSON.parse(JSON.stringify(colab.registos_por_dia || {}))
    }));
    
    // Removido setRenderKey - não é necessário
    
    // Debug: verificar se há duplicação nos dados
    colaboradoresClonados.forEach(colab => {
      const dias = Object.keys(colab.registos_por_dia || {});
      dias.forEach(dia => {
        const turnos = colab.registos_por_dia[dia];
        turnos.forEach((t, idx) => {
        });
      });
    });
    
    setColaboradoresHorarios(colaboradoresClonados);
    setEditingHorario(horario);
    setShowModal(true);
    
    // REMOVIDO: Busca automática - agora só carrega quando clicar no botão
  };

  const handleView = async (horario) => {
    // Abrir modal de visualização interativa
    setViewingHorario(horario);
    
    // Marcar horário como visto (para atualizar sinalética)
    try {
      const token = localStorage.getItem('token');
      await api.post(
        `${getBackendUrl()}/api/horarios-mensais/${horario.id}/marcar-visto`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Notificar o componente pai para atualizar contadores
      if (onHorarioAtualizado) {
        onHorarioAtualizado();
      }
    } catch (error) {
      console.error('Erro ao marcar horário como visto:', error);
    }
  };

  const zerarBancoHoras = () => {
    if (!window.confirm('⚠️ Zerar banco de horas de TODOS os colaboradores?\n\nIsso colocará "Banco Anterior" = 0 para todos.\n\nTem certeza?')) {
      return;
    }
    
    setColaboradoresHorarios(prev => prev.map(colab => ({
      ...colab,
      banco_horas_anterior: 0,
      banco_horas_anterior_manual: true
    })));
    
    alert('✅ Banco de horas zerado para todos colaboradores!');
  };
  
  // Estados para histórico de banco de horas
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);
  const [historicoData, setHistoricoData] = useState(null);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  
  const buscarBancoHorasAnterior = async () => {
    if (!periodoInicio) {
      alert('⚠️ Selecione o período primeiro!');
      return;
    }
    
    if (!window.confirm('📥 Buscar banco de horas do período anterior para TODOS os colaboradores?\n\nIsso irá preencher o campo "Banco H Anterior" com os valores acumulados.\n\nTem certeza?')) {
      return;
    }
    
    // IMPORTANTE: Verificar se há horários em memória que precisam ser salvos
    try {
      const token = localStorage.getItem('token');
      
      // Buscar TODOS os horários (incluindo os que podem estar apenas em memória de outras abas)
      const horariosResponse = await api.get(`${getBackendUrl()}/api/horarios-mensais`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Verificar se há horários anteriores ao período atual
      const horariosAnteriores = horariosResponse.data.filter(h => h.periodo_fim < periodoInicio);
      
      if (horariosAnteriores.length === 0) {
        
        const resposta = window.confirm(
          '⚠️ ATENÇÃO: Não foram encontrados horários anteriores salvos!\n\n' +
          'Isso significa que:\n' +
          '• O horário do período anterior não foi salvo ainda\n' +
          '• Ou este é o primeiro horário do sistema\n\n' +
          'Deseja continuar mesmo assim? (Os valores do Banco Anterior serão zerados)'
        );
        
        if (!resposta) {
          return;
        }
      }
    } catch (err) {
      console.error('[BUSCAR-BANCO] Erro ao verificar horários:', err);
    }
    
    try {
      const token = localStorage.getItem('token');
      let atualizados = 0;
      
      const novosColaboradores = await Promise.all(
        colaboradoresHorarios.map(async (colab) => {
          try {
            
            const response = await api.get(
              `${getBackendUrl()}/api/horarios-mensais/banco-horas-anterior/${colab.colaborador_id}`,
              {
                params: { data_referencia: periodoInicio },
                headers: { Authorization: `Bearer ${token}` }
              }
            );
            
            // REGRA CORRETA: O "Banco Ant" recebe o "Banco H FINAL" do período anterior
            // O Banco H final = banco_horas_anterior + banco_horas_periodo (do período anterior)
            // Usamos banco_horas_total que JÁ contém essa soma
            if (response.data.banco_horas_total !== undefined && response.data.data_encontrada) {
              atualizados++;
              // PRESERVAR MINUTOS: usar o valor exato (decimal) sem arredondar
              const valorComMinutos = response.data.banco_horas_total;
              return {
                ...colab,
                banco_horas_anterior: valorComMinutos,
                banco_horas_anterior_manual: false
              };
            }
            // Se não encontrou, manter valor atual
            return colab;
          } catch (err) {
            console.error(`[DEBUG] Erro ao buscar banco de horas para ${colab.colaborador_nome}:`, err);
            return colab;
          }
        })
      );
      
      setColaboradoresHorarios(novosColaboradores);
      
      // Mostrar alert informativo
      setTimeout(() => {
        if (atualizados > 0) {
          alert(`✅ Banco de horas anterior atualizado para ${atualizados} colaborador(es)!\n\n📊 Valores carregados do último horário PUBLICADO.`);
        } else {
          alert('⚠️ ATENÇÃO: Nenhum horário anterior PUBLICADO foi encontrado!\n\n' +
                'Os valores de "Banco Ant." foram zerados.\n\n' +
                '💡 Para carregar dados do horário anterior, certifique-se de que o horário anterior foi PUBLICADO/SALVO antes de criar um novo período.');
        }
      }, 300);
      
    } catch (err) {
      console.error('Erro ao buscar banco de horas anterior:', err);
      alert('❌ Erro ao buscar banco de horas anterior');
    }
  };
  
  const visualizarHistorico = async (colaborador) => {
    setLoadingHistorico(true);
    setShowHistoricoModal(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(
        `${getBackendUrl()}/api/horarios-mensais/historico-banco-horas/${colaborador.colaborador_id}`,
        {
          params: { limite: 12 },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setHistoricoData(response.data);
    } catch (err) {
      console.error('Erro ao buscar histórico:', err);
      alert('❌ Erro ao carregar histórico');
      setShowHistoricoModal(false);
    } finally {
      setLoadingHistorico(false);
    }
  };
  
  const exportarHistorico = async (colaboradorId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(
        `${getBackendUrl()}/api/horarios-mensais/exportar-historico-banco-horas/${colaboradorId}`,
        {
          params: { limite: 12 },
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Criar link de download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `historico_banco_horas_${colaboradorId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert('✅ Histórico exportado com sucesso!');
    } catch (err) {
      console.error('Erro ao exportar histórico:', err);
      alert('❌ Erro ao exportar histórico');
    }
  };
  
  const exportarHorarioAtual = async () => {
    if (!periodoInicio || !periodoFim) {
      toast.error('Define primeiro o período (início e fim) antes de exportar.');
      return;
    }
    if (!colaboradoresHorarios || colaboradoresHorarios.length === 0) {
      toast.error('Não há colaboradores neste horário para exportar.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const resp = await api.post(
        `${getBackendUrl()}/api/horarios-mensais/exportar-excel-semanal`,
        {
          periodo_inicio: periodoInicio,
          periodo_fim: periodoFim,
          colaboradores_horarios: colaboradoresHorarios,
          cabecalho: cabecalhoEditavel || '',
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );
      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Horario_${periodoInicio}_a_${periodoFim}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('✅ Horário exportado.');
    } catch (err) {
      console.error('Erro ao exportar horário:', err);
      toast.error(`Erro ao exportar: ${err.response?.data?.detail || err.message}`);
    }
  };

  // Carregar relatório de acessos de um horário
  const carregarRelatorioAcessos = async (horarioId) => {
    setLoadingRelatorio(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(
        `${getBackendUrl()}/api/horarios-mensais/${horarioId}/acessos`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRelatorioAcessos(response.data);
      setShowRelatorioAcessos(true);
    } catch (error) {
      console.error('Erro ao carregar relatório de acessos:', error);
      alert('Erro ao carregar relatório de acessos');
    }
    setLoadingRelatorio(false);
  };

  const handleLimpezaDados = async () => {
    const token = localStorage.getItem('token');
    
    try {
      // Primeiro, fazer diagnóstico
      if (!window.confirm('🔍 Deseja diagnosticar horários vazios?\n\nIsso irá analisar todos os horários SEM eliminar nada.')) {
        return;
      }
      
      const diagResponse = await api.get(
        `${getBackendUrl()}/api/horarios-mensais-admin/diagnostico-vazios`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const diag = diagResponse.data;
      const resumo = diag.resumo;
      
      // Mostrar diagnóstico
      let mensagem = `📊 DIAGNÓSTICO DE HORÁRIOS\n\n`;
      mensagem += `Total: ${resumo.total}\n`;
      mensagem += `✅ Válidos: ${resumo.validos}\n`;
      mensagem += `⚠️ Suspeitos: ${resumo.suspeitos}\n`;
      mensagem += `❌ Vazios: ${resumo.vazios}\n\n`;
      
      if (resumo.vazios > 0) {
        mensagem += `HORÁRIOS VAZIOS ENCONTRADOS:\n`;
        diag.horarios_vazios.forEach(h => {
          mensagem += `  • ${h.periodo} (${h.colaboradores} colab, ${h.total_turnos} turnos)\n`;
        });
        mensagem += `\n⚠️ Estes horários serão ELIMINADOS se continuar.`;
      } else {
        alert(mensagem + '\n✅ Nenhum horário vazio encontrado!\nSistema está limpo.');
        return;
      }
      
      // Confirmar limpeza
      if (!window.confirm(mensagem + '\n\n🗑️ Deseja ELIMINAR os horários vazios?')) {
        return;
      }
      
      // Executar limpeza
      const cleanResponse = await api.delete(
        `${getBackendUrl()}/api/horarios-mensais-admin/limpar-vazios`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const resultado = cleanResponse.data;
      
      let resultadoMsg = `✅ LIMPEZA CONCLUÍDA\n\n`;
      resultadoMsg += `Horários válidos preservados: ${resultado.horarios_validos}\n`;
      resultadoMsg += `Horários eliminados: ${resultado.horarios_eliminados}\n`;
      
      if (resultado.horarios_eliminados > 0) {
        resultadoMsg += `\nDetalhes:\n`;
        resultado.detalhes_eliminados.forEach(det => {
          resultadoMsg += `  • ${det}\n`;
        });
      }
      
      if (resultado.erros > 0) {
        resultadoMsg += `\n⚠️ Erros encontrados: ${resultado.erros}`;
      }
      
      alert(resultadoMsg);
      
      // Recarregar lista de horários
      loadHorarios();
      
    } catch (err) {
      console.error('Erro na manutenção:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Erro desconhecido';
      alert(`❌ Erro na manutenção de dados:\n\n${errorMsg}\n\nVerifique o console para mais detalhes.`);
    }
  };

  const copiarMesAnterior = async () => {
    if (!periodoInicio) {
      alert('⚠️ Selecione o período primeiro!');
      return;
    }
    
    if (!window.confirm('📋 Copiar turnos do mês anterior?\n\nIsso irá copiar todos os registos do mês anterior para este mês.\n\nTem certeza?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Calcular mês anterior
      const dataInicioObj = new Date(periodoInicio + 'T00:00:00');
      const mesAnterior = new Date(dataInicioObj.getFullYear(), dataInicioObj.getMonth() - 1, 1);
      const ultimoDiaMesAnterior = new Date(dataInicioObj.getFullYear(), dataInicioObj.getMonth(), 0);
      
      const mesAnteriorInicio = mesAnterior.toISOString().split('T')[0];
      const mesAnteriorFim = ultimoDiaMesAnterior.toISOString().split('T')[0];
      
      // Buscar horário anterior
      const response = await api.get(`${getBackendUrl()}/api/horarios-mensais`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const horarioAnterior = response.data.find(h => 
        h.periodo_inicio === mesAnteriorInicio && h.periodo_fim === mesAnteriorFim
      );
      
      if (!horarioAnterior) {
        alert('ℹ️ Não existe horário para o mês anterior');
        return;
      }
      
      // Copiar turnos ajustando as datas
      setColaboradoresHorarios(prev => prev.map(colab => {
        const colabAnterior = horarioAnterior.colaboradores_horarios?.find(
          c => c.colaborador_id === colab.colaborador_id
        );
        
        if (!colabAnterior || !colabAnterior.registos_por_dia) {
          return colab;
        }
        
        // Mapear datas do mês anterior para o mês atual (mesmo dia do mês)
        const novosRegistos = {};
        
        Object.keys(colabAnterior.registos_por_dia).forEach(dataAnterior => {
          const diaDoMes = new Date(dataAnterior + 'T00:00:00').getDate();
          
          // Criar data correspondente no mês atual
          const novaData = new Date(dataInicioObj.getFullYear(), dataInicioObj.getMonth(), diaDoMes);
          
          // Verificar se o dia existe no mês atual (ex: 31 pode não existir)
          if (novaData.getMonth() === dataInicioObj.getMonth()) {
            const novaDataStr = novaData.toISOString().split('T')[0];
            novosRegistos[novaDataStr] = colabAnterior.registos_por_dia[dataAnterior];
          }
        });
        
        return {
          ...colab,
          registos_por_dia: novosRegistos
        };
      }));
      
      alert('✅ Turnos copiados do mês anterior!');
    } catch (err) {
      console.error('Erro ao copiar mês anterior:', err);
      alert('❌ Erro ao copiar mês anterior');
    }
  };

  // Função para importar Excel
  const handleImportarExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Por favor selecione um ficheiro Excel (.xlsx ou .xls)');
      return;
    }

    setImportandoExcel(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      // Novo endpoint: entende o formato semanal com Café/Loja/Sátão
      const response = await api.post(
        `${getBackendUrl()}/api/horarios-mensais/importar-excel-semanal`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );

      const { periodo_inicio, periodo_fim, colaboradores_horarios: importados,
              estatisticas: stats, erros } = response.data || {};

      if (!importados || importados.length === 0) {
        toast.error('Excel processado mas sem turnos válidos. Verifica se os nomes dos colaboradores correspondem aos da BD.');
        if (stats?.nao_reconhecidos?.length) {
          window.alert('Nomes não reconhecidos:\n\n' + stats.nao_reconhecidos.join('\n'));
        }
        return;
      }

      const totalTurnos = stats?.total_turnos || 0;
      const nomes = stats?.colaboradores_encontrados || [];
      const naoRec = stats?.nao_reconhecidos || [];

      const msg =
        `📅 Período: ${periodo_inicio} → ${periodo_fim}\n` +
        `👥 Colaboradores reconhecidos (${nomes.length}): ${nomes.join(', ') || '—'}\n` +
        (naoRec.length ? `⚠️ Não reconhecidos (${naoRec.length}): ${naoRec.join(', ')}\n` : '') +
        `🕒 Turnos importados: ${totalTurnos}\n\n` +
        `Aplicar ao horário atual? (substitui os dados dos colaboradores importados)`;

      if (!window.confirm(msg)) {
        toast.info('Importação cancelada.');
        return;
      }

      // 1) Ajustar período
      if (periodo_inicio) setPeriodoInicio(periodo_inicio);
      if (periodo_fim) setPeriodoFim(periodo_fim);

      // 2) Merge: procura por id OU por nome normalizado (produção usa vários formatos de id)
      const normNome = (s) => (s || '')
        .toString()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase().replace(/\s+/g, ' ').trim();

      setColaboradoresHorarios(prev => {
        const list = Array.isArray(prev) ? [...prev] : [];
        importados.forEach(imp => {
          const impNome = normNome(imp.colaborador_nome);
          const impId = imp.colaborador_id;
          // procurar por id OU por nome normalizado
          const idx = list.findIndex(c =>
            c.colaborador_id === impId ||
            normNome(c.colaborador_nome) === impNome
          );
          if (idx >= 0) {
            const existente = list[idx];
            list[idx] = {
              ...existente,
              colaborador_id: existente.colaborador_id || impId,
              colaborador_nome: existente.colaborador_nome || imp.colaborador_nome,
              registos_por_dia: { ...(existente.registos_por_dia || {}), ...imp.registos_por_dia },
            };
          } else {
            list.push({
              colaborador_id: impId,
              colaborador_nome: imp.colaborador_nome,
              registos_por_dia: imp.registos_por_dia,
              banco_horas_anterior: 0,
              banco_horas: 0,
            });
          }
        });
        return list;
      });

      toast.success(`✅ Importados ${totalTurnos} turnos de ${nomes.length} colaborador(es).`);
      if (erros && erros.length) {
        console.warn('Excel import erros:', erros);
      }
    } catch (err) {
      console.error('Erro ao importar Excel:', err);
      toast.error(`Erro ao importar: ${err.response?.data?.detail || err.message}`);
    } finally {
      setImportandoExcel(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePrint = (horarioData = null, autoPrint = true) => {
    const inicio = horarioData?.periodo_inicio || periodoInicio;
    const fim = horarioData?.periodo_fim || periodoFim;
    const colabs = horarioData?.colaboradores_horarios || colaboradoresHorarios;
    const cabecalho = cabecalhoEditavel.replace(/\$\{inicio\}/g, inicio).replace(/\$\{fim\}/g, fim);
    
    const printHTML = generatePrintHTML({ inicio, fim, colaboradores: colabs, cabecalho });
    openPrintWindow(printHTML, autoPrint);
  };

  // Auto-save: Salvar rascunho automaticamente
  useEffect(() => {
    // Só auto-salvar se estiver em modo de edição e tiver dados mínimos
    if (showModal && colaboradoresHorarios.length > 0 && periodoInicio && periodoFim) {
      
      // Criar timer para auto-save após 5 segundos (mais rápido!)
      const timer = setTimeout(() => {
        handleAutoSave();
      }, 5000); // 5 segundos apenas!
      
      // Cleanup
      return () => {
        clearTimeout(timer);
      };
    }
  }, [colaboradoresHorarios, periodoInicio, periodoFim, showModal]);

  const handleAutoSave = async (silent = true) => {
    
    // CRÍTICO: Verificar se há dados reais para salvar
    const temDadosReais = colaboradoresHorarios.some(c => {
      const registos = c.registos_por_dia || {};
      const totalDias = Object.keys(registos).length;
      return totalDias > 0; // Só salvar se houver pelo menos 1 dia com turnos
    });
    
    if (!temDadosReais) {
      return;
    }
    
    try {
      await handleSave(true, silent); // true = é auto-save, silent = não mostrar toast
      setLastAutoSave(new Date());
    } catch (err) {
      console.error('[AUTO-SAVE] ❌ Erro:', err);
    }
  };

  const handleSave = async (isAutoSave = false, silent = false) => {
    // Evitar cliques múltiplos
    if (isSaving && !isAutoSave) {
      return;
    }
    
    if (!isAutoSave) {
      setIsSaving(true);
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // 🔧 CRÍTICO: Recalcular horas esperadas NO MOMENTO DO SALVAMENTO
      // para garantir que o valor está atualizado (não confiar no estado React)
      const horasEsperadasRecalculadas = calcularHorasEsperadas(periodoInicio, periodoFim);
      
      const colabsComTotal = colaboradoresHorarios.map(c => {
        const registosDias = Object.values(c.registos_por_dia || {});
        const todosRegistos = registosDias.flat(); // Flatten array de arrays
        
        const total = todosRegistos.reduce((sum, reg) => {
          // Contar horas de turnos com horas (normal, apoio, troca, reuniao, integracao, gestao)
          const gestaoDiaInteiro = reg.tipo === 'gestao' && !(reg.entrada && reg.saida);
          const contaHoras = !gestaoDiaInteiro && reg.horas && ['normal', 'apoio', 'troca', 'reuniao', 'integracao', 'gestao'].includes(reg.tipo);
          return sum + (contaHoras ? parseFloat(reg.horas) : 0);
        }, 0)
          // Gestão sem horário: 8h por dia único
          + (new Set(
              Object.entries(c.registos_por_dia || {})
                .filter(([_, ts]) => Array.isArray(ts) && ts.some(t => t.tipo === 'gestao' && !(t.entrada && t.saida)))
                .map(([d]) => d)
            ).size * 8)
          // Folga Compensação: 8h por cada dia único com este tipo
          + (new Set(
              Object.entries(c.registos_por_dia || {})
                .filter(([_, ts]) => Array.isArray(ts) && ts.some(t => t.tipo === 'folga_compensacao'))
                .map(([d]) => d)
            ).size * 8);

        const dias_ferias = todosRegistos.filter(r => r.tipo === 'ferias').length;
        const dias_falta = todosRegistos.filter(r => r.tipo === 'falta').length;
        const dias_folga = todosRegistos.filter(r => r.tipo === 'folga' || r.tipo === 'folga_compensacao').length;
        
        // 🔧 VERIFICAR se colaborador TEM turnos registados
        const temTurnos = Object.keys(c.registos_por_dia || {}).some(dia => {
          const turnos = c.registos_por_dia[dia] || [];
          return turnos.length > 0;
        });
        
        // 🔧 CORREÇÃO CRÍTICA: Se não tem turnos, ainda assim DEVE calcular o déficit!
        // Um colaborador que não trabalha DEVE HORAS ao sistema
        if (!temTurnos) {
          const bancoAnterior = c.banco_horas_anterior || 0;
          // Banco Período = 0h (trabalhadas) - horasEsperadas (esperadas) = NEGATIVO
          const bancoPeriodo = 0 - horasEsperadasRecalculadas;
          const bancoTotal = bancoAnterior + bancoPeriodo;
          
          return {
            ...c,
            total_horas: 0,
            banco_horas: bancoTotal, // 🔧 SALVAR o banco com déficit!
            banco_horas_periodo: bancoPeriodo, // 🔧 Déficit correto!
            dias_ferias,
            dias_falta,
            dias_folga
          };
        }
        
        // CALCULAR banco_horas (Banco H) APENAS para quem TEM turnos
        let totalHorasNormais = 0;
        let totalFeriado = 0;
        let totalReunioes = 0;
        const diasComFerias = new Set();
        const diasComGestao = new Set();
        const diasComBaixa = new Set();
        const diasComFolgaComp = new Set();

        Object.keys(c.registos_por_dia || {}).forEach(dia => {
          const turnos = c.registos_por_dia[dia] || [];
          const isFeriado = turnos.some(t => t.tipo === 'feriado');

          turnos.forEach(turno => {
            const horas = parseFloat(turno.horas || 0);
            if (turno.tipo === 'ferias') {
              diasComFerias.add(dia);
            } else if (turno.tipo === 'gestao') {
              // Gestão: se tem horas definidas (manual), conta como horas normais. Senão soma dia em diasComGestao.
              if (turno.entrada && turno.saida) {
                if (isFeriado) totalFeriado += horas; else totalHorasNormais += horas;
              } else {
                diasComGestao.add(dia);
              }
            } else if (turno.tipo === 'baixa') {
              diasComBaixa.add(dia);
            } else if (turno.tipo === 'folga_compensacao') {
              // Folga de compensação: conta 8h para banco (dia único mesmo com múltiplas entradas)
              diasComFolgaComp.add(dia);
            } else if (turno.tipo === 'reuniao') {
              totalReunioes += horas;
            } else if (turno.tipo === 'integracao' || turno.tipo === 'apoio' || turno.tipo === 'troca') {
              // Integração/Apoio/Troca contam como horas normais
              if (isFeriado) totalFeriado += horas; else totalHorasNormais += horas;
            } else if (isFeriado && turno.tipo === 'normal') {
              totalFeriado += horas;
            } else if (turno.tipo === 'normal') {
              totalHorasNormais += horas;
            }
          });
        });

        // REGRA PARA BANCO: Feriados contam em DOBRO até 2025, NORMAL a partir de 2026
        // Feriados contam como hora normal (×1). Folga Compensação = 8h/dia (como férias/baixa)
        const totalParaBanco = totalHorasNormais + totalFeriado + (diasComFerias.size * (8)) + (diasComGestao.size * 8) + (diasComBaixa.size * (8)) + (diasComFolgaComp.size * 8) + totalReunioes;
        const bancoAnterior = c.banco_horas_anterior || 0;
        // 🔧 CORREÇÃO: Usar valor RECALCULADO (não o estado React)
        const horasEsperadasLocal = horasEsperadasRecalculadas;
        // BANCO DO PERÍODO = Trabalhadas - Esperadas (se não trabalha, fica negativo)
        const bancoPeriodo = totalParaBanco - horasEsperadasLocal;
        
        return {
          ...c, 
          total_horas: total,
          banco_horas: bancoPeriodo + bancoAnterior, // 🔧 CORREÇÃO: SALVAR o TOTAL (anterior + período)
          banco_horas_periodo: bancoPeriodo, // Guardar também o banco do período
          dias_ferias,
          dias_falta,
          dias_folga
        };
      });
      
      const data = {
        periodo_inicio: periodoInicio,
        periodo_fim: periodoFim,
        horario_funcionamento: '07:00 - 20:30',
        horario_pessoal: '',
        horas_esperadas_periodo: horasEsperadasRecalculadas, // 🔧 Usar valor RECALCULADO
        status: isAutoSave ? 'rascunho' : 'rascunho', // Sempre salvar como rascunho inicialmente
        colaboradores_horarios: colabsComTotal
      };

      if (editingHorario) {
        // Timeout maior e retry para evitar erro 520
        const maxRetries = 3;
        let lastError = null;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            const response = await api.put(`${getBackendUrl()}/api/horarios-mensais/${editingHorario.id}`, data, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 30000 // 30 segundos de timeout
            });
            // Atualizar o editingHorario com o ID se for novo
            if (!editingHorario.id && response.data.id) {
              setEditingHorario({ ...editingHorario, id: response.data.id });
            }
            lastError = null;
            break; // Sucesso, sair do loop
          } catch (retryErr) {
            lastError = retryErr;
            if (attempt < maxRetries) {
              await new Promise(r => setTimeout(r, 1000 * attempt)); // Esperar antes de retry
            }
          }
        }
        
        if (lastError) {
          throw lastError;
        }
      } else {
        const response = await api.post(`${getBackendUrl()}/api/horarios-mensais`, data, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000 // 30 segundos de timeout
        });
        // Definir o editingHorario para que próximos saves façam PUT em vez de POST
        if (response.data.id) {
          setEditingHorario({ ...response.data, id: response.data.id });
        }
      }

      if (!isAutoSave) {
        setShowModal(false);
      }
      
      if (!silent) {
        loadHorarios();
      }
    } catch (err) {
      console.error('Erro ao salvar horário:', err);
      
      // Tentar extrair mensagem de erro de múltiplas formas
      const errorMessage = 
        err.response?.data?.detail || 
        err.response?.data?.message ||
        err.message ||
        'Erro ao salvar horário';
      
      alert(errorMessage);
    } finally {
      // Sempre resetar o estado de saving
      if (!isAutoSave) {
        setIsSaving(false);
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja eliminar este horário?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.delete(`${getBackendUrl()}/api/horarios-mensais/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadHorarios();
    } catch (err) {
      console.error('Erro ao eliminar horário:', err);
      alert('Erro ao eliminar horário');
    }
  };

  const updateSeccao = (colabId, seccao) => {
    setColaboradoresHorarios(prev => prev.map(c =>
      c.colaborador_id === colabId ? { ...c, seccao } : c
    ));
  };

  // Adicionar novo turno a um dia
  const adicionarTurno = (colabId, dia) => {
    setColaboradoresHorarios(prev => prev.map(c => {
      if (String(c.colaborador_id) !== String(colabId)) return c;
      
      const registosAtualizados = { ...c.registos_por_dia };
      if (!registosAtualizados[dia]) {
        registosAtualizados[dia] = [];
      }
      
      registosAtualizados[dia].push({
        local: c.seccao,
        entrada: null,
        saida: null,
        horas: null,
        tipo: 'normal',
        observacao: null
      });
      
      return { ...c, registos_por_dia: registosAtualizados };
    }));
  };
  
  // Mostrar menu rápido ao clicar em célula vazia
  const mostrarMenuRapido = (event, colabId, dia, seccao) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setQuickMenuPosition({
      top: rect.bottom,
      left: rect.left
    });
    setQuickMenuData({ colabId, dia, seccao });
  };
  
  // Aplicar template de turno
  const aplicarTemplate = (template) => {
    const { colabId, dia, seccao } = quickMenuData;
    
    // Salvar referência do estado de troca antes de fechar
    const trocaMarcada = isTrocaQuickMenu;
    
    // FECHAR MENU - usando estado normal (sem flushSync)
    setQuickMenuPosition(null);
    setQuickMenuData(null);
    setIsTrocaQuickMenu(false); // Reset checkbox
    
    // DEBUG: Log se troca foi marcada
    if (trocaMarcada) {
    }
    
    // APOIO/REUNIÃO/INTEGRAÇÃO/GESTÃO: preservar turnos existentes e adicionar nova linha
    // para o user digitar as horas. Permite combinar (ex: turno normal 7-13 + APOIO 14-15)
    const TIPOS_HORAS_MANUAIS = ['apoio', 'reuniao', 'integracao', 'gestao'];
    if (TIPOS_HORAS_MANUAIS.includes(template.tipo)) {
      const prefixoPorTipo = {
        'apoio': 'APOIO ',
        'reuniao': 'REUNIÃO ',
        'integracao': 'INTEGRAÇÃO ',
        'gestao': 'GESTÃO ',
      };
      const prefixo = prefixoPorTipo[template.tipo] || '';
      setTimeout(() => {
        const textarea = document.querySelector(`textarea[data-colab="${colabId}"][data-dia="${dia}"][data-seccao="${seccao}"]`);
        if (textarea) {
          const valorActual = (textarea.value || '').trim();
          // Se já tem conteúdo, adicionar nova linha com o prefixo
          textarea.value = valorActual ? `${valorActual}\n${prefixo}` : prefixo;
          textarea.focus();
          textarea.selectionStart = textarea.selectionEnd = textarea.value.length;
        }
      }, 150);
      return;
    }
    
    // Para outros tipos, atualizar os horários normalmente
    setColaboradoresHorarios(prev => prev.map(c => {
      if (String(c.colaborador_id) !== String(colabId)) return c;
      
      const registosAtualizados = { ...c.registos_por_dia };
      if (!registosAtualizados[dia]) {
        registosAtualizados[dia] = [];
      }
      
      let novoTurno = {
        local: seccao,
        tipo: template.tipo,
        observacao: null,
        e_troca: trocaMarcada
      };
      
      // DEBUG: Log para verificar
      
      // Para turnos normais com entrada/saida diretas (novos templates)
      if (template.entrada && template.saida && template.tipo === 'normal') {
        novoTurno.entrada = template.entrada;
        novoTurno.saida = template.saida;
        novoTurno.horas = parseFloat(calcularHoras(template.entrada, template.saida));
      }
      // Para turnos normais com horário string "HH:MM-HH:MM" (formato antigo)
      else if (template.horario && (template.tipo === 'normal' || template.tipo === 'troca')) {
        const [entrada, saida] = template.horario.split('-');
        novoTurno.entrada = entrada;
        novoTurno.saida = saida;
        novoTurno.horas = parseFloat(calcularHoras(entrada, saida));
      }
      // Para gestão (8h automáticas)
      else if (template.tipo === 'gestao') {
        novoTurno.entrada = null;
        novoTurno.saida = null;
        novoTurno.horas = 8.0;
      }
      // Para baixa, férias e folga_compensacao (8h automáticas)
      else if (template.tipo === 'baixa' || template.tipo === 'ferias' || template.tipo === 'folga_compensacao') {
        novoTurno.entrada = null;
        novoTurno.saida = null;
        novoTurno.horas = 8;
      }
      // Para apoio (precisa definir horas manualmente)
      else if (template.tipo === 'apoio') {
        novoTurno.entrada = null;
        novoTurno.saida = null;
        novoTurno.horas = null;
      }
      // Para folga, falta (sem horas)
      else {
        novoTurno.entrada = null;
        novoTurno.saida = null;
        novoTurno.horas = null;
      }
      
      // ADICIONAR turno (permitir múltiplos turnos no mesmo dia/secção)
      // NÃO remover turnos existentes - permite 2+ turnos no mesmo dia
      registosAtualizados[dia].push(novoTurno);
      
      return { ...c, registos_por_dia: registosAtualizados };
    }));
    
    // LOG para debug
  };

  // Remover turno de um dia
  const removerTurno = (colabId, dia, turnoIndex) => {
    setColaboradoresHorarios(prev => prev.map(c => {
      if (String(c.colaborador_id) !== String(colabId)) return c;
      
      const registosAtualizados = { ...c.registos_por_dia };
      if (registosAtualizados[dia]) {
        registosAtualizados[dia].splice(turnoIndex, 1);
        if (registosAtualizados[dia].length === 0) {
          delete registosAtualizados[dia];
        }
      }
      
      return { ...c, registos_por_dia: registosAtualizados };
    }));
  };

  // Adicionar turno rápido com botão de atalho
  const adicionarTurnoRapido = (colabId, dia, tipo, seccao) => {
    const template = TEMPLATES_TURNOS.find(t => t.tipo === tipo);
    if (!template) return;
    
    setColaboradoresHorarios(prev => prev.map(c => {
      if (String(c.colaborador_id) !== String(colabId)) return c;
      
      const registosAtualizados = { ...c.registos_por_dia };
      if (!registosAtualizados[dia]) {
        registosAtualizados[dia] = [];
      }
      
      let novoTurno = {
        local: seccao,
        tipo: template.tipo,
        observacao: null
      };
      
      if (template.horario && (template.tipo === 'normal' || template.tipo === 'troca')) {
        const [entrada, saida] = template.horario.split('-');
        novoTurno.entrada = entrada;
        novoTurno.saida = saida;
        novoTurno.horas = parseFloat(calcularHoras(entrada, saida));
      } else if (template.tipo === 'gestao') {
        novoTurno.horas = 8.0;
      } else if (template.tipo === 'baixa' || template.tipo === 'ferias') {
        novoTurno.horas = 8;  // 6h40m
      }
      
      registosAtualizados[dia].push(novoTurno);
      
      return { ...c, registos_por_dia: registosAtualizados };
    }));
  };
  
  // Processar horário simplificado "09:00-17:00"
  const processarHorarioSimplificado = (colabId, dia, turnoIndex, horarioStr) => {
    if (!horarioStr || !horarioStr.includes('-')) return;
    
    const [entrada, saida] = horarioStr.split('-').map(h => h.trim());
    
    setColaboradoresHorarios(prev => prev.map(c => {
      if (String(c.colaborador_id) !== String(colabId)) return c;
      
      const registosAtualizados = { ...c.registos_por_dia };
      if (!registosAtualizados[dia] || !registosAtualizados[dia][turnoIndex]) return c;
      
      registosAtualizados[dia][turnoIndex].entrada = entrada;
      registosAtualizados[dia][turnoIndex].saida = saida;
      registosAtualizados[dia][turnoIndex].horas = parseFloat(calcularHoras(entrada, saida));
      
      return { ...c, registos_por_dia: registosAtualizados };
    }));
  };
  
  // Atualizar campo de um turno específico
  const updateRegistoTurno = (colabId, dia, turnoIndex, field, valor) => {
    setColaboradoresHorarios(prev => prev.map(c => {
      if (String(c.colaborador_id) !== String(colabId)) return c;
      
      const registosAtualizados = { ...c.registos_por_dia };
      if (!registosAtualizados[dia] || !registosAtualizados[dia][turnoIndex]) return c;
      
      registosAtualizados[dia][turnoIndex][field] = valor;
      
      // Recalcular horas se entrada e saída estiverem preenchidas
      const turno = registosAtualizados[dia][turnoIndex];
      
      // Gestão: horas reais se tem entrada/saída (ou horas manuais), senão 8h
      if (turno.tipo === 'gestao') {
        if (field === 'horas' && valor !== null) {
          turno.horas = parseFloat(valor) || 8.0;
        } else if (turno.entrada && turno.saida) {
          turno.horas = parseFloat(calcularHoras(turno.entrada, turno.saida));
        } else {
          turno.horas = 8.0;
        }
      }
      // Se for tipo "baixa" ou "ferias", sempre manter 6h40m
      else if (turno.tipo === 'baixa' || turno.tipo === 'ferias') {
        turno.horas = 8;
      } 
      // Se for tipo manual (apoio/reuniao/integracao/gestao), horas calculadas via entrada/saída ou definidas manualmente
      else if (['apoio', 'reuniao', 'integracao', 'gestao'].includes(turno.tipo)) {
        if (field === 'horas' && valor !== null) {
          turno.horas = parseFloat(valor) || null;
        } else if (turno.entrada && turno.saida) {
          turno.horas = parseFloat(calcularHoras(turno.entrada, turno.saida));
        }
      }
      // Para normal e troca, calcular baseado em entrada/saída
      else if (turno.entrada && turno.saida) {
        turno.horas = parseFloat(calcularHoras(turno.entrada, turno.saida));
      } else {
        turno.horas = null;
      }
      
      return { ...c, registos_por_dia: registosAtualizados };
    }));
  };

  const days = periodoInicio && periodoFim ? getDaysInPeriod(periodoInicio, periodoFim) : [];

  // Função helper para agrupar dias em semanas de CALENDÁRIO (Segunda a Domingo)
  // Cada semana começa em Segunda e termina em Domingo
  const agruparPorSemanasCalendario = (diasArray) => {
    if (!diasArray || diasArray.length === 0) return [];
    
    // Primeiro, garantir que os dias estão ordenados e únicos
    const diasUnicos = [...new Set(diasArray)].sort();
    
    const semanas = [];
    let semanaAtual = [];
    
    for (const dia of diasUnicos) {
      const date = new Date(dia + 'T00:00:00');
      const diaDaSemana = date.getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
      
      // Se é SEGUNDA e já temos dias na semana anterior, fechar essa semana
      if (diaDaSemana === 1 && semanaAtual.length > 0) {
        semanas.push([...semanaAtual]);
        semanaAtual = [];
      }
      
      // Adicionar dia à semana atual
      semanaAtual.push(dia);
      
      // Se é DOMINGO, fechar a semana atual
      if (diaDaSemana === 0 && semanaAtual.length > 0) {
        semanas.push([...semanaAtual]);
        semanaAtual = [];
      }
    }
    
    // Adicionar última semana se tiver dias (caso não termine em Domingo)
    if (semanaAtual.length > 0) {
      semanas.push(semanaAtual);
    }
    
    return semanas;
  };

  const colaboradoresPorSeccao = SECCOES.map(seccao => ({
    seccao,
    colaboradores: colaboradoresHorarios.filter(c => c.ativo !== false && c.seccao === seccao && c.mostrar_em_horario !== false)
  }));

  const recalcularTodosHorarios = async () => {
    if (!window.confirm('🔄 Recalcular TODOS os horários salvos?\n\nIsso irá aplicar as novas regras:\n- Feriados contam como hora normal\n- Férias/Baixas 6h40m/dia\n\nTem certeza?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(
        `${getBackendUrl()}/api/horarios-mensais/recalcular-todos`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`✅ ${response.data.message}`);
      loadHorarios(); // Recarregar lista
    } catch (err) {
      console.error('Erro ao recalcular:', err);
      alert('❌ Erro ao recalcular horários');
    }
  };

  if (loading) return <div className="text-center py-8">Carregando...</div>;

  // Toggle de publicação do horário
  const togglePublicacao = async (horarioId, publicadoAtual) => {
    try {
      const novoEstado = !publicadoAtual;
      const token = localStorage.getItem('token');
      
      // Remover /api do início pois api.patch já adiciona
      const url = `/horarios-mensais/${horarioId}/publicar?publicado=${novoEstado}`;
      
      await api.patch(
        url,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Atualizar lista localmente
      setHorarios(prev => prev.map(h => 
        h.id === horarioId ? { ...h, publicado: novoEstado } : h
      ));
      
      const status = novoEstado ? "publicado" : "rascunho";
      alert(`✅ Horário marcado como ${status}!`);
      
      // Chamar callback para atualizar badge (quando publicar/despublicar)
      if (onHorarioAtualizado) {
        onHorarioAtualizado();
      }
    } catch (error) {
      console.error('Erro ao alterar publicação:', error);
      alert('❌ Erro ao alterar visibilidade do horário');
    }
  };

  return (
    <>
      <style>{`
        @media print {
          /* Esconder tudo exceto a modal */
          body > *:not(#modal-visualizacao) {
            display: none !important;
          }
          
          .hide-on-print,
          .no-print {
            display: none !important;
          }
          
          /* Configurar página */
          @page {
            size: A4 landscape;
            margin: 5mm;
          }
          
          /* Ajustar modal para impressão */
          #modal-visualizacao {
            transform: scale(0.68);
            transform-origin: top left;
          }
          
          /* Ajustar tamanhos das tabelas */
          .print-area table {
            font-size: 5.5pt !important;
            border-collapse: collapse !important;
          }
          
          .print-area th,
          .print-area td {
            padding: 2px !important;
            font-size: 5.5pt !important;
            border: 1px solid #999 !important;
          }
          
          .print-area h2 {
            font-size: 12pt !important;
            margin-bottom: 5px !important;
          }
          
          .print-area .bg-gray-50 {
            background: #f0f0f0 !important;
            border: 1px solid #ccc !important;
            font-size: 6pt !important;
            padding: 4px !important;
            margin-bottom: 4px !important;
            white-space: pre-wrap !important;
            line-height: 1.3 !important;
          }
          
          /* Evitar quebras de página */
          .print-area table {
            page-break-inside: avoid;
          }
          
          /* Garantir que cores sejam impressas */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
      <div className="space-y-6 hide-on-print">
        {/* ModuleInfo - Ajuda contextual */}
        <div className="flex items-center gap-2">
          <ModuleInfo
            descricao="Gestao completa de horarios semanais dos colaboradores. Permite criar, editar, publicar e visualizar horarios mensais. Na vista 'Gestao', e possivel definir modelos reutilizaveis e publicar semanas especificas."
            dados="Horarios guardados no MongoDB (colecao horarios). Colaboradores carregados de /api/users. Modelos e semanas via /api/horarios/modelos e /api/horarios/semanas."
            calculo="Horas esperadas = semanas do periodo x 40h. Banco de horas = total trabalhado - horas esperadas. Herda banco do horario anterior publicado."
            legendas="Vista Atual = horarios mensais em uso. Gestao = modelos e semanas para preparacao. Publicado = visivel para colaboradores. Rascunho = apenas admin."
          />
        </div>
        {/* Toggle entre Sistema Atual e Gestão de Horários — REMOVIDO: apenas Horários Mensais */}

        {/* ==================== VISTA DE HORÁRIOS MENSAIS ==================== */}
        {viewModeHorario === 'gestao' && isAdmin && (
          <div className="space-y-4">
            
            {/* ==================== MODO EDIÇÃO (Semana ou Modelo) ==================== */}
            <HorarioGestaoEditor
              modoEdicaoGestao={modoEdicaoGestao}
              semanaGestaoEditando={semanaGestaoEditando}
              modeloGestaoEditando={modeloGestaoEditando}
              semanaModeloAtual={semanaModeloAtual}
              setSemanaModeloAtual={setSemanaModeloAtual}
              periodoInicio={periodoInicio}
              setPeriodoInicio={setPeriodoInicio}
              periodoFim={periodoFim}
              setPeriodoFim={setPeriodoFim}
              colaboradoresHorarios={colaboradoresHorarios}
              setColaboradoresHorarios={setColaboradoresHorarios}
              isSaving={isSaving}
              setModeloGestaoEditando={setModeloGestaoEditando}
              setQuickMenuPosition={setQuickMenuPosition}
              setQuickMenuData={setQuickMenuData}
              cancelarEdicaoGestao={cancelarEdicaoGestao}
              guardarSemanaEditor={guardarSemanaEditor}
              guardarModeloEditor={guardarModeloEditor}
              getColaboradoresDisponiveis={getColaboradoresDisponiveis}
              removerColaboradorDoHorario={removerColaboradorDoHorario}
              adicionarColaboradorAoHorario={adicionarColaboradorAoHorario}
              moverColaboradorCima={moverColaboradorCima}
              moverColaboradorBaixo={moverColaboradorBaixo}
              formatarDataCurta={formatarDataCurta}
              colaboradoresAtivos={colaboradoresAtivos}
              calcularHoras={calcularHoras}
            />
            
            {/* ==================== LISTA DE GESTÃO (quando não está em modo edição) ==================== */}
            {!modoEdicaoGestao && (
              <HorarioGestaoLista
                loadingGestao={loadingGestao}
                gestaoSubTab={gestaoSubTab}
                setGestaoSubTab={setGestaoSubTab}
                modelos={modelos}
                loadingModelos={loadingModelos}
                editarModelo={editarModelo}
                criarNovoModelo={novoModelo}
                eliminarModelo={eliminarModelo}
                showCopiarModeloDialog={showCopiarModeloDialog}
                setShowCopiarModeloDialog={setShowCopiarModeloDialog}
                showCopiarModeloHorarioDialog={showCopiarModeloHorarioDialog}
                setShowCopiarModeloHorarioDialog={setShowCopiarModeloHorarioDialog}
                modeloParaCopiar={modeloParaCopiar}
                setModeloParaCopiar={setModeloParaCopiar}
                semanaModeloIdx={semanaModeloIdx}
                setSemanaModeloIdx={setSemanaModeloIdx}
                semanaDestinoIdx={semanaDestinoIdx}
                setSemanaDestinoIdx={setSemanaDestinoIdx}
                getSemanasDoMes={getSemanasDoMes}
                aplicarModeloAoHorarioMensal={aplicarModeloAoHorarioMensal}
                handleImportarExcel={handleImportarExcel}
                importandoExcel={importandoExcel}
                executarCopiaDeModelo={executarCopiaDeModelo}
                semanaGestaoEditando={semanaGestaoEditando}
                formatarDataCurta={formatarDataCurta}
              />
            )}

          </div>
        )}

        {/* ==================== VISTA ACTUAL ==================== */}
        {viewModeHorario === 'atual' && (
          <HorarioVistaAtual
            readOnly={readOnly}
            isAdmin={isAdmin}
            user={user}
            horarios={horarios}
            horarioData={null}
            periodoInicio={periodoInicio}
            setPeriodoInicio={setPeriodoInicio}
            periodoFim={periodoFim}
            setPeriodoFim={setPeriodoFim}
            colaboradoresHorarios={colaboradoresHorarios}
            setColaboradoresHorarios={setColaboradoresHorarios}
            cabecalhoEditavel={cabecalhoEditavel}
            setCabecalhoEditavel={setCabecalhoEditavel}
            isSaving={isSaving}
            showModal={showModal}
            setShowModal={setShowModal}
            showRelatorioAcessos={showRelatorioAcessos}
            setShowRelatorioAcessos={setShowRelatorioAcessos}
            showHistoricoModal={showHistoricoModal}
            setShowHistoricoModal={setShowHistoricoModal}
            showModalCabecalho={showModalCabecalho}
            setShowModalCabecalho={setShowModalCabecalho}
            historicoData={historicoData}
            setHistoricoData={setHistoricoData}
            modalTurno={modalTurno}
            setModalTurno={setModalTurno}
            viewingHorario={viewingHorario}
            setViewingHorario={setViewingHorario}
            isTrocaModal={isTrocaModal}
            setIsTrocaModal={setIsTrocaModal}
            isTrocaQuickMenu={isTrocaQuickMenu}
            setIsTrocaQuickMenu={setIsTrocaQuickMenu}
            criarHorarioMensal={handleNew}
            adicionarColaboradorAoHorario={adicionarColaboradorAoHorario}
            removerColaboradorDoHorario={removerColaboradorDoHorario}
            moverColaboradorCima={moverColaboradorCima}
            moverColaboradorBaixo={moverColaboradorBaixo}
            quickMenuPosition={quickMenuPosition}
            setQuickMenuPosition={setQuickMenuPosition}
            quickMenuData={quickMenuData}
            setQuickMenuData={setQuickMenuData}
            getColaboradoresDisponiveis={getColaboradoresDisponiveis}
            calcularSemanas={calcularSemanas}
            colaboradoresAtivos={colaboradoresAtivos}
            formatarDataCurta={formatarDataCurta}
            getSemanasDoMes={getSemanasDoMes}
            onHorarioAtualizado={onHorarioAtualizado}
            showCopiarModeloHorarioDialog={showCopiarModeloHorarioDialog}
            setShowCopiarModeloHorarioDialog={setShowCopiarModeloHorarioDialog}
            modeloParaCopiar={modeloParaCopiar}
            setModeloParaCopiar={setModeloParaCopiar}
            modelos={modelos}
            semanaDestinoIdx={semanaDestinoIdx}
            setSemanaDestinoIdx={setSemanaDestinoIdx}
            aplicarModeloAoHorarioMensal={aplicarModeloAoHorarioMensal}
            handleNew={handleNew}
            handleView={handleView}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handlePrint={handlePrint}
            handleLimpezaDados={handleLimpezaDados}
            handleSave={handleSave}
            carregarRelatorioAcessos={carregarRelatorioAcessos}
            togglePublicacao={togglePublicacao}
            abrirCopiarModeloHorarioMensal={abrirCopiarModeloHorarioMensal}
            copiarMesAnterior={copiarMesAnterior}
            buscarBancoHorasAnterior={buscarBancoHorasAnterior}
            zerarBancoHoras={zerarBancoHoras}
            exportarHistorico={exportarHistorico}
            visualizarHistorico={visualizarHistorico}
            handleImportarExcel={handleImportarExcel}
            importandoExcel={importandoExcel}
            exportarHorarioAtual={exportarHorarioAtual}
            addTurno={addTurno}
            calcularHoras={calcularHoras}
            carregarCabecalho={carregarCabecalho}
            aplicarTemplate={aplicarTemplate}
            agruparPorSemanasCalendario={agruparPorSemanasCalendario}
            removerTurno={removerTurno}
            editingHorario={editingHorario}
            setEditingHorario={setEditingHorario}
            fileInputRef={fileInputRef}
            loading={loading}
            relatorioAcessos={relatorioAcessos}
            loadingHistorico={loadingHistorico}
            modoExcel={modoExcel}
            lastAutoSave={lastAutoSave}
            loadingRelatorio={loadingRelatorio}
            semanas={semanas}
            horasEsperadas={horasEsperadas}
            days={days}
          />
        )}

      {/* MODAL DE VISUALIZAÇÃO DE HORÁRIO - Componente Extraído */}
      <HorarioVisualizacaoModal
        show={!!viewingHorario}
        viewingHorario={viewingHorario}
        cabecalhoEditavel={cabecalhoEditavel}
        readOnly={readOnly}
        onClose={() => setViewingHorario(null)}
        onEditCabecalho={() => setShowModalCabecalho(true)}
        onPrint={handlePrint}
      />
      
      {/* MODAL ÚNICO DE ADICIONAR TURNO - Componente Extraído */}
      <HorarioAdicionarTurnoModal
        show={!!modalTurno}
        modalTurno={modalTurno}
        isTrocaModal={isTrocaModal}
        setIsTrocaModal={setIsTrocaModal}
        calcularHoras={calcularHoras}
        addTurno={addTurno}
        setColaboradoresHorarios={setColaboradoresHorarios}
        onClose={() => setModalTurno(null)}
      />
      
      {/* MODAL DE EDIÇÃO DE CABEÇALHO - Componente Extraído */}
      <HorarioCabecalhoModal
        show={showModalCabecalho}
        cabecalhoEditavel={cabecalhoEditavel}
        onCabecalhoChange={setCabecalhoEditavel}
        onClose={() => {
          setShowModalCabecalho(false);
          carregarCabecalho();
        }}
        onSave={async () => {
          try {
            const token = localStorage.getItem('token');
            await api.post(`${getBackendUrl()}/api/configuracoes/cabecalho-horarios`, 
              { cabecalho: cabecalhoEditavel },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            // Também guardar no localStorage como backup
            localStorage.setItem('cabecalho_impressao', cabecalhoEditavel);
            setShowModalCabecalho(false);
            alert('✅ Cabeçalho guardado com sucesso! Todos os utilizadores verão esta alteração.');
          } catch (error) {
            console.error('Erro ao guardar cabeçalho:', error);
            alert('❌ Erro ao guardar cabeçalho. Tente novamente.');
          }
        }}
      />
      
      {/* MODAL DE HISTÓRICO DE BANCO DE HORAS - Componente Extraído */}
      <HorarioHistoricoBancoModal
        show={showHistoricoModal}
        loading={loadingHistorico}
        historicoData={historicoData}
        onClose={() => {
          setShowHistoricoModal(false);
          setHistoricoData(null);
        }}
        onExportar={exportarHistorico}
      />

      {/* MODAL RELATÓRIO DE ACESSOS - Componente Extraído */}
      <HorarioRelatorioAcessosModal
        show={showRelatorioAcessos}
        relatorioAcessos={relatorioAcessos}
        onClose={() => setShowRelatorioAcessos(false)}
      />
      </div>
    </>
  );
};

export default HorarioTab;
